import React from "react";
import { ArrowLeft, Clock, User } from "lucide-react";
import { Button } from "./ui/button";

const BlogPost3 = ({ onBack }) => {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Button 
        variant="ghost" 
        onClick={onBack}
        className="mb-8 text-credentra-bright-teal hover:text-credentra-dark-teal"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Blog
      </Button>
      
      <article className="prose prose-lg max-w-none">
        <header className="mb-8">
          <h1 className="text-4xl font-bold text-credentra-dark-teal mb-4">
            What CPAs Need from Tip-Credit Workpapers (and How to Deliver It Fast)
          </h1>
          <div className="flex items-center gap-4 text-gray-600 text-sm">
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              5 min read
            </div>
            <div className="flex items-center gap-1">
              <User className="h-4 w-4" />
              Credentra Team
            </div>
          </div>
        </header>

        <div className="space-y-6 text-gray-700 leading-relaxed">
          <p className="text-xl text-gray-600 font-medium">
            As a CPA, you know tip credits can add real value for restaurant clients. But you also know the documentation requirements can turn a straightforward credit into a time-consuming project. Here's what you actually need to make the process efficient and audit-proof.
          </p>

          <h2 className="text-2xl font-bold text-credentra-dark-teal mt-8 mb-4">The Documentation Challenge</h2>
          <p>
            FICA tip credits require more supporting documentation than most credits. The IRS wants to see:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Proof of tips received by employees</li>
            <li>Evidence that employer FICA taxes were actually paid on those tips</li>
            <li>Reconciliation between POS data and payroll records</li>
            <li>Documentation of any adjustments or assumptions made</li>
          </ul>

          <p>
            The problem? Most restaurants hand you a shoebox of POS reports and payroll summaries and expect you to piece everything together. That's where the real work begins.
          </p>

          <h2 className="text-2xl font-bold text-credentra-dark-teal mt-8 mb-4">What Makes Good Workpapers</h2>
          <p>
            After reviewing hundreds of tip credit calculations, here's what separates the good workpapers from the headache-inducing ones:
          </p>

          <h3 className="text-xl font-bold text-credentra-dark-teal mt-6 mb-3">1. Clear Source Documentation</h3>
          <p>
            Every number should trace back to its source. That means:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Monthly POS summaries with tip totals</li>
            <li>Corresponding payroll registers showing reported tips</li>
            <li>Bank statements confirming credit card tip distributions</li>
            <li>Documentation of cash tip reporting procedures</li>
          </ul>

          <h3 className="text-xl font-bold text-credentra-dark-teal mt-6 mb-3">2. Reconciliation Workings</h3>
          <p>
            POS tips never match payroll tips exactly. Good workpapers show:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Starting point: Total tips from POS system</li>
            <li>Less: Credit card processing fees</li>
            <li>Less: Unreported cash tips (with explanation)</li>
            <li>Less: Tip pooling adjustments</li>
            <li>Equals: Tips subject to employer FICA tax</li>
          </ul>

          <h3 className="text-xl font-bold text-credentra-dark-teal mt-6 mb-3">3. FICA Tax Calculation Detail</h3>
          <p>
            Show your work on the tax calculation:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Reported tips by month</li>
            <li>Employer Social Security tax (6.2%)</li>
            <li>Employer Medicare tax (1.45%)</li>
            <li>Total employer FICA tax eligible for credit</li>
          </ul>

          <div className="bg-gray-50 p-6 rounded-lg my-6">
            <h3 className="font-bold text-credentra-dark-teal mb-3">Pro Tip:</h3>
            <p>
              Create a monthly summary schedule. It's much easier to review twelve monthly numbers than trying to understand a single annual calculation. Plus, it makes it easier to spot unusual months that might need explanation.
            </p>
          </div>

          <h2 className="text-2xl font-bold text-credentra-dark-teal mt-8 mb-4">Common Red Flags to Address</h2>
          <p>
            These are the issues that will definitely get questions from the IRS (or from you):
          </p>

          <h3 className="text-xl font-bold text-credentra-dark-teal mt-6 mb-3">Tip Reporting Seems Too Perfect</h3>
          <p>
            If payroll shows tips that are exactly 15% of sales every month, that's a red flag. Real tip reporting has variation. Document the client's actual tip reporting procedures and any shortfall allocations.
          </p>

          <h3 className="text-xl font-bold text-credentra-dark-teal mt-6 mb-3">Large Differences Between POS and Payroll</h3>
          <p>
            If POS shows $10,000 in tips but payroll shows $6,000, explain the $4,000 difference. Common reasons include unreported cash tips, tip pooling, or credit card fees, but document which one applies.
          </p>

          <h3 className="text-xl font-bold text-credentra-dark-teal mt-6 mb-3">Missing Months of Data</h3>
          <p>
            Don't just skip months where you don't have POS data. Note the missing months and explain how you handled them (excluded from credit calculation, estimated based on other months, etc.).
          </p>

          <h2 className="text-2xl font-bold text-credentra-dark-teal mt-8 mb-4">Automation Changes Everything</h2>
          <p>
            Here's the reality: Manual tip credit calculations take too long to be profitable for most CPA firms. You're either spending hours on data gathering and reconciliation, or you're not offering the service at all.
          </p>

          <p>
            Automated systems change this equation by delivering exactly what you need:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Pre-reconciled data with source references</li>
            <li>Standardized workpaper formats</li>
            <li>Built-in reasonableness checks</li>
            <li>Export-ready documentation for your tax software</li>
          </ul>

          <h2 className="text-2xl font-bold text-credentra-dark-teal mt-8 mb-4">Making It Work for Your Practice</h2>
          <p>
            The best approach varies by practice, but here's what consistently works:
          </p>

          <ol className="list-decimal pl-6 space-y-3">
            <li><strong>Set client expectations early</strong> - Explain what data you'll need and when you'll need it</li>
            <li><strong>Use standardized procedures</strong> - Whether manual or automated, consistency reduces errors and review time</li>
            <li><strong>Document your assumptions</strong> - Future you (and the IRS) will appreciate knowing why you made certain choices</li>
            <li><strong>Keep it simple</strong> - Complex calculations are more likely to have errors and harder to defend</li>
          </ol>

          <div className="bg-credentra-bright-teal/10 border-l-4 border-credentra-bright-teal p-6 my-8">
            <h3 className="font-semibold text-credentra-dark-teal mb-2">The Real Win:</h3>
            <p>
              Good tip credit workpapers aren't just about compliance—they're about building trust with restaurant clients. When you can show them exactly how much they're saving and why they qualify for the credit, you become the CPA who actually understands their business.
            </p>
          </div>

          <h2 className="text-2xl font-bold text-credentra-dark-teal mt-8 mb-4">Beyond the Current Year</h2>
          <p>
            Don't forget about amended returns. Many restaurants have been missing this credit for years. Good workpapers make it easy to apply the same methodology to prior years, potentially uncovering significant additional refunds for clients.
          </p>

          <p>
            Just remember the three-year limitation for refund claims, and be extra thorough with documentation for amended returns since they're more likely to get IRS attention.
          </p>

          <h2 className="text-2xl font-bold text-credentra-dark-teal mt-8 mb-4">The Bottom Line</h2>
          <p>
            Tip credit workpapers don't have to be complicated, but they do need to be complete. Whether you're calculating manually or using automation, focus on creating documentation that tells a clear story: here's what the client reported, here's what they paid in taxes, and here's why they qualify for this credit.
          </p>

          <p>
            Get that foundation right, and both you and your clients can feel confident about claiming every dollar they're entitled to.
          </p>
        </div>
      </article>
    </div>
  );
};

export default BlogPost3;